import { pipe, map } from "wonka";
import { assert, GadgetConnection, AuthenticationMode, InternalModelManager, enqueueActionRunner, BackgroundActionHandle } from "@gadgetinc/api-client-core";
import { buildGlobalAction } from "./builder.js";
import { SessionManager } from "./models/Session.js";
import { CurrentSessionManager } from "./models/CurrentSession.js";
import { ShopifyGdprRequestManager } from "./models/ShopifyGdprRequest.js";
import { ShopifyShopManager } from "./models/ShopifyShop.js";
import { ShopifySyncManager } from "./models/ShopifySync.js";
import { SubscriptionPlanManager } from "./models/SubscriptionPlan.js";
import { ShopifyAppSubscriptionManager } from "./models/ShopifyAppSubscription.js";
import { ShopifyAppManager } from "./models/ShopifyApp.js";
import { LeakedCodeManager } from "./models/LeakedCode.js";
import { DiscountLinksManager } from "./models/DiscountLinks.js";
import { ShopifyBulkOperationManager } from "./models/ShopifyBulkOperation.js";
import { DuplicativeCustomerDiscountBlockedManager } from "./models/DuplicativeCustomerDiscountBlocked.js";
import { DevNamespace } from "./namespaces/dev.js";
import { InternalscriptsNamespace } from "./namespaces/internalScripts.js";
import { MiddlewareNamespace } from "./namespaces/middleware.js";
import { OnetimerunNamespace } from "./namespaces/oneTimeRun.js";
import { SandboxNamespace } from "./namespaces/sandbox.js";
import { DefaultSessionSelection as DefaultSessionSelection2 } from "./models/Session.js";
import { DefaultShopifyGdprRequestSelection as DefaultShopifyGdprRequestSelection2 } from "./models/ShopifyGdprRequest.js";
import { DefaultShopifyShopSelection as DefaultShopifyShopSelection2 } from "./models/ShopifyShop.js";
import { DefaultShopifySyncSelection as DefaultShopifySyncSelection2 } from "./models/ShopifySync.js";
import { DefaultSubscriptionPlanSelection as DefaultSubscriptionPlanSelection2 } from "./models/SubscriptionPlan.js";
import { DefaultShopifyAppSubscriptionSelection as DefaultShopifyAppSubscriptionSelection2 } from "./models/ShopifyAppSubscription.js";
import { DefaultShopifyAppSelection as DefaultShopifyAppSelection2 } from "./models/ShopifyApp.js";
import { DefaultLeakedCodeSelection as DefaultLeakedCodeSelection2 } from "./models/LeakedCode.js";
import { DefaultDiscountLinksSelection as DefaultDiscountLinksSelection2 } from "./models/DiscountLinks.js";
import { DefaultShopifyBulkOperationSelection as DefaultShopifyBulkOperationSelection2 } from "./models/ShopifyBulkOperation.js";
import { DefaultDuplicativeCustomerDiscountBlockedSelection as DefaultDuplicativeCustomerDiscountBlockedSelection2 } from "./models/DuplicativeCustomerDiscountBlocked.js";
const productionEnv = "production";
const fallbackEnv = "staging";
const getImplicitEnv = () => {
  try {
    return process.env.GADGET_ENV;
  } catch (error) {
    return void 0;
  }
};
class KeepcartServerClient {
  constructor(options) {
    this.options = options;
    /** Executes the alertNewInjectedCode global action. */
    this.alertNewInjectedCode = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "alertNewInjectedCode",
      operationName: "alertNewInjectedCode",
      operationReturnType: "AlertNewInjectedCode",
      namespace: null,
      variables: {}
    });
    /** Executes the createDiscountFunction global action. */
    this.createDiscountFunction = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createDiscountFunction",
      operationName: "createDiscountFunction",
      operationReturnType: "CreateDiscountFunction",
      namespace: null,
      variables: { shopId: { required: false, type: "String" } }
    });
    /** Executes the deleteDiscountFunction global action. */
    this.deleteDiscountFunction = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "deleteDiscountFunction",
      operationName: "deleteDiscountFunction",
      operationReturnType: "DeleteDiscountFunction",
      namespace: null,
      variables: { shopId: { required: false, type: "String" } }
    });
    /** Executes the deployWebPixelToBlockingStores global action. */
    this.deployWebPixelToBlockingStores = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "deployWebPixelToBlockingStores",
      operationName: "deployWebPixelToBlockingStores",
      operationReturnType: "DeployWebPixelToBlockingStores",
      namespace: null,
      variables: {}
    });
    /** Executes the getReport global action. */
    this.getReport = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getReport",
      operationName: "getReport",
      operationReturnType: "GetReport",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        startDate: { required: false, type: "String" },
        endDate: { required: false, type: "String" }
      }
    });
    /** Executes the globalShopifySync global action. */
    this.globalShopifySync = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "globalShopifySync",
      operationName: "globalShopifySync",
      operationReturnType: "GlobalShopifySync",
      namespace: null,
      variables: {
        apiKeys: { required: false, type: "[String!]" },
        syncSince: { required: false, type: "DateTime" },
        models: { required: false, type: "[String!]" },
        force: { required: false, type: "Boolean" },
        startReason: { required: false, type: "String" }
      }
    });
    /** @deprecated The action scheduledShopifySync does not have an api trigger and cannot be called from this api client. If you are the developer of this application and want api clients to call this action add an api trigger to the action. For more information see: https://docs.gadget.dev/guides/actions/triggers */
    this.scheduledShopifySync = buildGlobalAction(this, {
      type: "stubbedAction",
      functionName: "scheduledShopifySync",
      actionApiIdentifier: "scheduledShopifySync",
      dataPath: "scheduledShopifySync",
      errorMessage: "The action scheduledShopifySync does not have an api trigger and cannot be called from this api client. If you are the developer of this application and want api clients to call this action add an api trigger to the action. For more information see: https://docs.gadget.dev/guides/actions/triggers",
      variables: {},
      reason: "MissingApiTrigger"
    });
    /** Executes the sendReports global action. */
    this.sendReports = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendReports",
      operationName: "sendReports",
      operationReturnType: "SendReports",
      namespace: null,
      variables: {}
    });
    /** Executes the sendScannedLeakAlertEmail global action. */
    this.sendScannedLeakAlertEmail = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendScannedLeakAlertEmail",
      operationName: "sendScannedLeakAlertEmail",
      operationReturnType: "SendScannedLeakAlertEmail",
      namespace: null,
      variables: {}
    });
    /** Executes the setBlockingReplaceCode global action. */
    this.setBlockingReplaceCode = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setBlockingReplaceCode",
      operationName: "setBlockingReplaceCode",
      operationReturnType: "SetBlockingReplaceCode",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        blockingReplaceCode: { required: false, type: "String" }
      }
    });
    /** Executes the setCustomerTagExclusion global action. */
    this.setCustomerTagExclusion = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setCustomerTagExclusion",
      operationName: "setCustomerTagExclusion",
      operationReturnType: "SetCustomerTagExclusion",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        customerTagExclusionList: { required: false, type: "[String!]" }
      }
    });
    /** Executes the setDuplicateBlockingMode global action. */
    this.setDuplicateBlockingMode = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setDuplicateBlockingMode",
      operationName: "setDuplicateBlockingMode",
      operationReturnType: "SetDuplicateBlockingMode",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        duplicateBlockingMode: { required: false, type: "String" }
      }
    });
    /** Executes the setExclusionList global action. */
    this.setExclusionList = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setExclusionList",
      operationName: "setExclusionList",
      operationReturnType: "SetExclusionList",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        exclusionList: { required: false, type: "[String!]" }
      }
    });
    /** Executes the setExtensionBlockingMode global action. */
    this.setExtensionBlockingMode = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setExtensionBlockingMode",
      operationName: "setExtensionBlockingMode",
      operationReturnType: "SetExtensionBlockingMode",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        extensionBlockingMode: { required: false, type: "String" }
      }
    });
    /** Executes the updateCodeStatus global action. */
    this.updateCodeStatus = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateCodeStatus",
      operationName: "updateCodeStatus",
      operationReturnType: "UpdateCodeStatus",
      namespace: null,
      variables: {}
    });
    /** Executes the updateGadgetLeakedCodesWithTinybirdBlockedCodes global action. */
    this.updateGadgetLeakedCodesWithTinybirdBlockedCodes = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updateGadgetLeakedCodesWithTinybirdBlockedCodes",
      operationName: "updateGadgetLeakedCodesWithTinybirdBlockedCodes",
      operationReturnType: "UpdateGadgetLeakedCodesWithTinybirdBlockedCodes",
      namespace: null,
      variables: {}
    });
    /** Executes the webPixelControl global action. */
    this.webPixelControl = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "webPixelControl",
      operationName: "webPixelControl",
      operationReturnType: "WebPixelControl",
      namespace: null,
      variables: {
        shopId: { required: false, type: "String" },
        connect: { required: false, type: "Boolean" }
      }
    });
    /**
     * The list of environments with a customized API root endpoint
     */
    this.apiRoots = { "production": "https://keepcart-server.gadget.app/" };
    this.applicationId = "25657";
    /** Start a transaction against the Gadget backend which will atomically commit (or rollback). */
    this.transaction = async (callback) => {
      return await this.connection.transaction(callback);
    };
    /**
    * Get a new direct upload token for file uploads to directly to cloud storage.
    * See https://docs.gadget.dev/guides/storing-files#direct-uploads-using-tokens for more information
    * @return { url: string, token: string } A `url` to upload one file to, and a token for that file to pass back to Gadget as an action input.
    */
    this.getDirectUploadToken = async () => {
      const result = await this.query("query GetDirectUploadToken($nonce: String) { gadgetMeta { directUploadToken(nonce: $nonce) { url, token } } }", { nonce: Math.random().toString(36).slice(2, 7) }, {
        requestPolicy: "network-only"
      });
      return result.gadgetMeta.directUploadToken;
    };
    let inSSRContext = false;
    try {
      inSSRContext = !!(import.meta.env && import.meta.env.SSR);
    } catch (error) {
    }
    if (inSSRContext) {
      const api = globalThis.GadgetGlobals?.api;
      if (api) {
        return api.actAsSession;
      }
    }
    this.environment = (options?.environment ?? getImplicitEnv() ?? fallbackEnv).toLocaleLowerCase();
    let apiRoot;
    if (this.apiRoots[this.environment]) {
      apiRoot = this.apiRoots[this.environment];
    } else {
      const envPart = this.environment == productionEnv ? "" : `--${this.environment}`;
      apiRoot = `https://keepcart-server${envPart}.gadget.app`;
    }
    const exchanges = { ...options?.exchanges };
    if (this.environment !== productionEnv) {
      const devHarnessExchange = ({ forward }) => {
        return (operations$) => {
          const operationResult$ = forward(operations$);
          return pipe(
            operationResult$,
            map((result) => {
              try {
                if (typeof window !== "undefined" && typeof CustomEvent === "function") {
                  const event = new CustomEvent("gadget:devharness:graphqlresult", { detail: result });
                  window.dispatchEvent(event);
                }
              } catch (error) {
                console.warn("[gadget] error dispatching gadget dev harness event", error);
              }
              return result;
            })
          );
        };
      };
      exchanges.beforeAll = [
        devHarnessExchange,
        ...exchanges.beforeAll ?? []
      ];
    }
    this.connection = new GadgetConnection({
      endpoint: new URL("api/graphql", apiRoot).toString(),
      applicationId: this.applicationId,
      authenticationMode: options?.authenticationMode ?? (typeof window == "undefined" ? { anonymous: true } : { browserSession: true }),
      ...options,
      exchanges,
      environment: this.environment
    });
    if (typeof window != "undefined" && this.connection.authenticationMode == AuthenticationMode.APIKey && !options?.authenticationMode?.dangerouslyAllowBrowserApiKey) {
      throw new Error("GGT_BROWSER_API_KEY_USAGE: Using a Gadget API key to authenticate this client object is insecure and will leak your API keys to attackers. Please use a different authentication mode.");
    }
    if (typeof options?.authenticationMode === "undefined" && typeof window !== "undefined" && window.shopify?.idToken) {
      this.connection.setAuthenticationMode({
        custom: {
          async processFetch(_input, init) {
            const headers = new Headers(init.headers);
            const idToken = await window.shopify.idToken();
            headers.append("Authorization", "ShopifySessionToken " + idToken);
            init.headers ?? (init.headers = {});
            headers.forEach(function(value, key) {
              init.headers[key] = value;
            });
          },
          async processTransactionConnectionParams(params) {
            const idToken = await window.shopify.idToken();
            params.auth.shopifySessionToken = idToken;
          }
        }
      });
    }
    this.session = new SessionManager(this.connection);
    this.currentSession = new CurrentSessionManager(this.connection);
    this.shopifyGdprRequest = new ShopifyGdprRequestManager(this.connection);
    this.shopifyShop = new ShopifyShopManager(this.connection);
    this.shopifySync = new ShopifySyncManager(this.connection);
    this.subscriptionPlan = new SubscriptionPlanManager(this.connection);
    this.shopifyAppSubscription = new ShopifyAppSubscriptionManager(this.connection);
    this.shopifyApp = new ShopifyAppManager(this.connection);
    this.leakedCode = new LeakedCodeManager(this.connection);
    this.discountLinks = new DiscountLinksManager(this.connection);
    this.shopifyBulkOperation = new ShopifyBulkOperationManager(this.connection);
    this.duplicativeCustomerDiscountBlocked = new DuplicativeCustomerDiscountBlockedManager(this.connection);
    this.dev = new DevNamespace(this);
    this.internalScripts = new InternalscriptsNamespace(this);
    this.middleware = new MiddlewareNamespace(this);
    this.oneTimeRun = new OnetimerunNamespace(this);
    this.sandbox = new SandboxNamespace(this);
    this.internal = {
      session: new InternalModelManager("session", this.connection, { "pluralApiIdentifier": "sessions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyGdprRequest: new InternalModelManager("shopifyGdprRequest", this.connection, { "pluralApiIdentifier": "shopifyGdprRequests", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyShop: new InternalModelManager("shopifyShop", this.connection, { "pluralApiIdentifier": "shopifyShops", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifySync: new InternalModelManager("shopifySync", this.connection, { "pluralApiIdentifier": "shopifySyncs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      subscriptionPlan: new InternalModelManager("subscriptionPlan", this.connection, { "pluralApiIdentifier": "subscriptionPlans", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyAppSubscription: new InternalModelManager("shopifyAppSubscription", this.connection, { "pluralApiIdentifier": "shopifyAppSubscriptions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyApp: new InternalModelManager("shopifyApp", this.connection, { "pluralApiIdentifier": "shopifyApps", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      leakedCode: new InternalModelManager("leakedCode", this.connection, { "pluralApiIdentifier": "leakedCodes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      discountLinks: new InternalModelManager("discountLinks", this.connection, { "pluralApiIdentifier": "discountLinkss", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      shopifyBulkOperation: new InternalModelManager("shopifyBulkOperation", this.connection, { "pluralApiIdentifier": "shopifyBulkOperations", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      duplicativeCustomerDiscountBlocked: new InternalModelManager("duplicativeCustomerDiscountBlocked", this.connection, { "pluralApiIdentifier": "duplicativeCustomerDiscountBlockeds", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      dev: {
        seeds: {}
      },
      internalScripts: {},
      middleware: {},
      oneTimeRun: {},
      sandbox: {}
    };
  }
  /**
   * Returns a new Client instance that will call the Gadget API as the application's admin user.
   * This can only be used for API clients using internal authentication.
   * @returns {KeepcartServerClient} A new KeepcartServerClient instance with admin authentication
   */
  get actAsAdmin() {
    assert(this.options?.authenticationMode?.internal, `actAsAdmin can only be used for API clients using internal authentication, this client is using ${JSON.stringify(this.options?.authenticationMode)}`);
    return new KeepcartServerClient({
      ...this.options,
      authenticationMode: {
        internal: {
          ...this.options.authenticationMode.internal,
          actAsSession: false
        }
      }
    });
  }
  /**
   * Returns a new KeepcartServerClient instance that will call the Gadget API as with the permission of the current session.
   * This can only be used for API clients using internal authentication.
   * @returns {KeepcartServerClient} A new KeepcartServerClient instance with session authentication
   */
  get actAsSession() {
    assert(this.options?.authenticationMode?.internal, "actAsSession can only be used for API clients using internal authentication");
    return new KeepcartServerClient({
      ...this.options,
      authenticationMode: {
        internal: {
          ...this.options.authenticationMode.internal,
          actAsSession: true
        }
      }
    });
  }
  /** Run an arbitrary GraphQL query. */
  async query(graphQL, variables, options) {
    const { data, error } = await this.connection.currentClient.query(graphQL, variables, options).toPromise();
    if (error)
      throw error;
    return data;
  }
  /** Run an arbitrary GraphQL mutation. */
  async mutate(graphQL, variables, options) {
    const { data, error } = await this.connection.currentClient.mutation(graphQL, variables, options).toPromise();
    if (error)
      throw error;
    return data;
  }
  /**
   * `fetch` function that works the same as the built-in `fetch` function, but automatically passes authentication information for this API client.
   *
   * @example
   * await api.fetch("https://myapp--development.gadget.app/foo/bar");
   *
   * @example
   * // fetch a relative URL from the endpoint this API client is configured to fetch from
   * await api.fetch("/foo/bar");
   **/
  async fetch(input, init = {}) {
    return await this.connection.fetch(input, init);
  }
  async enqueue(action, inputOrOptions, maybeOptions) {
    assert(action, ".enqueue must be passed an action as the first argument but was passed undefined");
    let input;
    let options;
    if (typeof maybeOptions !== "undefined") {
      input = inputOrOptions;
      options = maybeOptions;
    } else if (!action.variables || Object.keys(action.variables).length == 0) {
      input = {};
      options = inputOrOptions;
    } else {
      if (typeof inputOrOptions == "string") {
        input = { id: inputOrOptions };
      } else {
        input = inputOrOptions;
      }
      options = {};
    }
    return await enqueueActionRunner(this.connection, action, input, options);
  }
  /**
   * Returns a handle for a given background action id
   *
   * @param action The action that was enqueued
   * @param id The id of the background action
   *
   * @example
   * const handle = api.handle(api.widget.update, "app-job-12346");
   *
   * @example
   * const handle = api.handle(api.someGlobalAction, "app-job-56789");
   **/
  handle(action, id) {
    return new BackgroundActionHandle(this.connection, action, id);
  }
  toString() {
    return `KeepcartServerClient<${this.environment}>`;
  }
  toJSON() {
    return this.toString();
  }
}
KeepcartServerClient.prototype[Symbol.for("gadget/modelRelationships")] = { "session": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyGdprRequest": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyShop": { "syncs": { "type": "HasMany", "model": "shopifySync" }, "gdprRequests": { "type": "HasMany", "model": "shopifyGdprRequest" }, "subscription": { "type": "BelongsTo", "model": "subscriptionPlan" }, "appSubscriptions": { "type": "HasMany", "model": "shopifyAppSubscription" }, "leakedCodes": { "type": "HasMany", "model": "leakedCode" }, "discountLinks": { "type": "HasMany", "model": "discountLinks" }, "bulkOperations": { "type": "HasMany", "model": "shopifyBulkOperation" } }, "shopifySync": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "subscriptionPlan": { "shop": { "type": "HasMany", "model": "shopifyShop" } }, "shopifyAppSubscription": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyApp": {}, "leakedCode": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "discountLinks": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "shopifyBulkOperation": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } }, "duplicativeCustomerDiscountBlocked": { "shop": { "type": "BelongsTo", "model": "shopifyShop" } } };
const Client = KeepcartServerClient;
export {
  Client,
  DefaultDiscountLinksSelection2 as DefaultDiscountLinksSelection,
  DefaultDuplicativeCustomerDiscountBlockedSelection2 as DefaultDuplicativeCustomerDiscountBlockedSelection,
  DefaultLeakedCodeSelection2 as DefaultLeakedCodeSelection,
  DefaultSessionSelection2 as DefaultSessionSelection,
  DefaultShopifyAppSelection2 as DefaultShopifyAppSelection,
  DefaultShopifyAppSubscriptionSelection2 as DefaultShopifyAppSubscriptionSelection,
  DefaultShopifyBulkOperationSelection2 as DefaultShopifyBulkOperationSelection,
  DefaultShopifyGdprRequestSelection2 as DefaultShopifyGdprRequestSelection,
  DefaultShopifyShopSelection2 as DefaultShopifyShopSelection,
  DefaultShopifySyncSelection2 as DefaultShopifySyncSelection,
  DefaultSubscriptionPlanSelection2 as DefaultSubscriptionPlanSelection,
  KeepcartServerClient
};
//# sourceMappingURL=Client.js.map
